import React from "react";
import { Alert, Box, Button, Link, Stack, Typography, useTheme } from "@mui/material";
import PlanModel from "../../../models/plan.model";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";

// AlertItem kept as named export (used elsewhere via import)
export const AlertItem = Alert;

type PlanItemProps = {
    plan: PlanModel
    dueDatePlan: number
    userHasPlan: boolean
    isActualPlan: boolean
    planInCheck: boolean
    onPlanSelected: () => void,
    onOneTimePurchase?: () => void,
    onChangeCard?: () => void,
    onCancelPlan?: () => void,
    isPreapproval: boolean,
    isPlanCancelled: boolean,
    isPlanPreapproval: boolean
    isPromotional?: boolean
}

export const PlanItem: React.FC<PlanItemProps> = ({
    plan,
    isActualPlan,
    planInCheck,
    dueDatePlan,
    onPlanSelected,
    onOneTimePurchase,
    onChangeCard,
    onCancelPlan,
    isPreapproval,
    isPlanCancelled,
    isPlanPreapproval,
    isPromotional,
}) => {
    const theme = useTheme();

    const getMonth = (value?: number) => {
        if (value) return Math.max(1, Math.round(value / 30));
        else return 1;
    };

    const formatCurrency = (value: number) => {
        return value.toLocaleString("pt-br", { style: "currency", currency: "BRL" });
    };

    // Planos são por duração: o card mostra o preço/mês equivalente e, abaixo,
    // o total cobrado no período (a cada N meses).
    const months = getMonth(plan.daysOfUse);
    const displayPrice = plan.value / months;

    const billingNote =
        months <= 1
            ? "cobrado mensalmente"
            : months === 12
            ? `${formatCurrency(plan.value)} cobrado anualmente`
            : `${formatCurrency(plan.value)} cobrado a cada ${months} meses`;

    const isHighlighted = isActualPlan;

    return (
        <Box
            sx={{
                position: "relative",
                height: "100%",
                bgcolor: "background.paper",
                border: isHighlighted
                    ? `2px solid ${theme.palette.primary.main}`
                    : `1px solid ${theme.palette.divider}`,
                borderRadius: "18px",
                p: "28px 26px",
                display: "flex",
                flexDirection: "column",
                boxShadow: isHighlighted
                    ? theme.customShadows.cardLg
                    : theme.customShadows.card,
            }}
        >
            {/* Badge "SEU PLANO ATUAL" */}
            {isActualPlan && (
                <Box
                    sx={{
                        position: "absolute",
                        top: -14,
                        left: "50%",
                        transform: "translateX(-50%)",
                        bgcolor: "primary.main",
                        color: "primary.contrastText",
                        borderRadius: "999px",
                        px: 1.5,
                        py: 0.25,
                        fontSize: "0.6875rem",
                        fontWeight: 700,
                        letterSpacing: 0.8,
                        textTransform: "uppercase",
                        whiteSpace: "nowrap",
                    }}
                >
                    Seu plano atual
                </Box>
            )}

            {/* Promotional badge */}
            {isPromotional && !isActualPlan && (
                <Box
                    sx={{
                        position: "absolute",
                        top: -14,
                        left: "50%",
                        transform: "translateX(-50%)",
                        bgcolor: "primary.main",
                        color: "primary.contrastText",
                        borderRadius: "999px",
                        px: 1.5,
                        py: 0.25,
                        fontSize: "0.6875rem",
                        fontWeight: 700,
                        letterSpacing: 0.8,
                        textTransform: "uppercase",
                        whiteSpace: "nowrap",
                    }}
                >
                    Promocional
                </Box>
            )}

            {/* Plan name */}
            <Typography
                sx={{
                    fontSize: "0.9375rem",
                    fontWeight: 600,
                    color: isActualPlan ? "primary.main" : "text.primary",
                    mb: 1,
                }}
            >
                {plan.name}
            </Typography>

            {/* Price */}
            <Box sx={{ mb: 2.5 }}>
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                    <Typography
                        component="span"
                        sx={{
                            fontFamily: (t) => t.typography.fontFamilyDisplay,
                            fontSize: "2.5rem",
                            fontWeight: 400,
                            lineHeight: 1,
                            color: "text.primary",
                        }}
                    >
                        {formatCurrency(displayPrice)}
                    </Typography>
                    <Typography
                        component="span"
                        sx={{ fontSize: "0.8125rem", color: "text.secondary" }}
                    >
                        /mês
                    </Typography>
                </Box>
                <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", mt: 0.5 }}>
                    {billingNote}
                </Typography>
            </Box>

            {/* CTA buttons */}
            <Stack spacing={1} sx={{ mb: 2.5 }}>
                {isPlanPreapproval ? (
                    <>
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            sx={{ height: 46, borderRadius: "12px" }}
                            onClick={() => onPlanSelected()}
                        >
                            Assinar plano
                        </Button>
                        <Button
                            fullWidth
                            variant="outlined"
                            color="primary"
                            sx={{ height: 46, borderRadius: "12px" }}
                            onClick={() => onOneTimePurchase?.()}
                        >
                            Compra avulsa
                        </Button>
                    </>
                ) : (
                    <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        sx={{ height: 46, borderRadius: "12px" }}
                        onClick={() => onPlanSelected()}
                    >
                        {isActualPlan ? "Recomprar plano" : "Assinar plano"}
                    </Button>
                )}

                {/* Alterar cartão — only for current preapproval active plan */}
                {isActualPlan && dueDatePlan >= 0 && !planInCheck && isPreapproval && !isPlanCancelled && (
                    <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        sx={{ height: 46, borderRadius: "12px" }}
                        onClick={() => onChangeCard?.()}
                    >
                        Alterar cartão
                    </Button>
                )}
            </Stack>

            {/* Features list */}
            {plan.features && plan.features.length > 0 && (
                <Box component="ul" sx={{ p: 0, m: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 1 }}>
                    {plan.features.map((feat, idx) => (
                        <Box
                            key={idx}
                            component="li"
                            sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                        >
                            <CheckRoundedIcon
                                sx={{
                                    color: "primary.main",
                                    fontSize: 17,
                                    mt: "2px",
                                    flexShrink: 0,
                                }}
                            />
                            <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>
                                {feat}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}

            {/* Expiry warning + cancel link — only for current active plan */}
            {isActualPlan && dueDatePlan >= 0 && !planInCheck && (
                <Stack spacing={1} sx={{ mt: 2 }}>
                    <Alert severity="warning" variant="outlined" sx={{ fontSize: "0.8125rem" }}>
                        Seu plano expira em {dueDatePlan} dias!
                    </Alert>
                    {isPreapproval && !isPlanCancelled && (
                        <Link
                            component="button"
                            variant="body2"
                            sx={{ color: "error.main", textAlign: "center" }}
                            onClick={() => onCancelPlan?.()}
                        >
                            Cancelar assinatura
                        </Link>
                    )}
                </Stack>
            )}

            {/* Awaiting approval */}
            {isActualPlan && planInCheck && (
                <Alert severity="error" variant="outlined" sx={{ mt: 2, fontSize: "0.8125rem" }}>
                    Aguardando aprovação!
                </Alert>
            )}
        </Box>
    );
};
